import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Check, ClipboardCheck, Send, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  OPL_TYPE_LABELS,
  PERMISSIONS,
  type OplStatus,
  type OplType,
} from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  DetailRow,
  Field,
  Input,
  Modal,
  QualityWarning,
  SafetyWarning,
  Select,
  Spinner,
  StatusPill,
  oplStatusTone,
} from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAuth, useCan } from '../../lib/auth';
import { formatDate, formatDateTime, humanise } from '../../lib/format';
import { queue } from '../../lib/offline/sync';

interface OplDetail {
  opl: {
    id: string;
    oplNo: string;
    title: string;
    oplType: string;
    status: string;
    machineId: string | null;
    sourceProblemId: string | null;
  };
  version: {
    id: string;
    revision: number;
    title: string;
    oplType: string;
    learningTimeMin: number | null;
    description: string | null;
    keyPoints: string[] | null;
    safetyInfo: string | null;
    qualityInfo: string | null;
    status: string;
    changeNote: string | null;
    preparedByName: string | null;
    approvedByName: string | null;
    approvedAt: string | null;
    publishedAt: string | null;
    effectiveDate: string | null;
    postedLocation: string | null;
    isCurrent: boolean;
  };
  revisions: Array<{
    id: string;
    revision: number;
    status: string;
    isCurrent: boolean;
    changeNote: string | null;
    createdAt: string;
  }>;
  media: Array<{ id: string; mediaId: string; role: string; caption: string | null }>;
  machine: { id: string; code: string; name: string } | null;
  line: { id: string; name: string } | null;
  linkedMachines: Array<{ id: string; code: string; name: string }>;
  linkedItems: Array<{ itemId: string; seq: number; task: string }>;
  trainingSummary: Record<string, number>;
  allowedTransitions: OplStatus[];
}

const TRANSITION_LABEL: Record<string, string> = {
  SUBMITTED: 'Submit for review',
  IN_REVIEW: 'Start review',
  APPROVED: 'Approve',
  PUBLISHED: 'Publish',
  ARCHIVED: 'Archive',
  DRAFT: 'Send back to draft',
};

export function OplDetailPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const versionId = params.get('versionId') ?? '';
  const queryClient = useQueryClient();
  const user = useAuth((s) => s.user);
  const [error, setError] = useState<string | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [completed, setCompleted] = useState(false);

  const canAssign = useCan(PERMISSIONS.TRAINING_ASSIGN);
  const canCompleteOwn = useCan(PERMISSIONS.TRAINING_COMPLETE_OWN);

  const { data, isLoading } = useQuery({
    queryKey: ['opl', id, versionId],
    queryFn: () => api.get<OplDetail>(`/api/opls/${id}${versionId ? `?versionId=${versionId}` : ''}`),
  });

  const transition = useMutation({
    mutationFn: (to: OplStatus) =>
      api.post(`/api/opls/versions/${data!.version.id}/transition`, { to }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'That change could not be made.'),
  });

  const completeTraining = useMutation({
    mutationFn: async () => {
      // Queued so an operator can finish a lesson at the machine with no signal.
      await queue('opl.training.complete', id, { signedName: user?.fullName });
    },
    onSuccess: () => {
      setCompleted(true);
      void queryClient.invalidateQueries({ queryKey: ['training'] });
    },
  });

  if (isLoading || !data) return <Spinner label="Loading lesson" />;

  const { opl, version } = data;
  const isDraftLike = ['DRAFT', 'SUBMITTED', 'IN_REVIEW'].includes(version.status);

  return (
    <div>
      <PageHeader
        title={`${opl.oplNo} — ${version.title}`}
        back={{ to: '/opl', label: 'All lessons' }}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge>{OPL_TYPE_LABELS[version.oplType as OplType] ?? humanise(version.oplType)}</Badge>
            <StatusPill tone={oplStatusTone(version.status)} label={humanise(version.status)} />
            <span>Revision {version.revision}</span>
            {version.learningTimeMin ? <span>· {version.learningTimeMin} min to learn</span> : null}
            {!version.isCurrent ? <Badge tone="warn">Not the current revision</Badge> : null}
          </span>
        }
        actions={
          <>
            {canAssign && opl.status === 'PUBLISHED' ? (
              <Button variant="secondary" icon={Users} onClick={() => setAssignOpen(true)}>
                Assign training
              </Button>
            ) : null}
            {data.allowedTransitions.map((to) => (
              <Button
                key={to}
                variant={to === 'PUBLISHED' || to === 'APPROVED' ? 'primary' : 'secondary'}
                icon={to === 'SUBMITTED' ? Send : Check}
                loading={transition.isPending && transition.variables === to}
                onClick={() => transition.mutate(to)}
              >
                {TRANSITION_LABEL[to] ?? humanise(to)}
              </Button>
            ))}
          </>
        }
      />

      <div className="grid gap-4 p-4 lg:grid-cols-3 lg:p-6">
        <div className="space-y-4 lg:col-span-2">
          {error ? <Callout tone="alarm">{error}</Callout> : null}

          {isDraftLike ? (
            <Callout tone="info" title="Not published yet">
              Operators cannot be assigned this lesson until it is approved and published.
            </Callout>
          ) : null}

          {opl.sourceProblemId ? (
            <Callout tone="info" title="This lesson came from a problem">
              <Link to={`/problems/${opl.sourceProblemId}`} className="font-bold underline">
                See the original problem tag →
              </Link>
            </Callout>
          ) : null}

          {/* The OPL sheet itself — laid out like the paper original. */}
          <Card>
            <CardHeader
              title="One-Point Lesson"
              subtitle={`${opl.oplNo} · ${data.machine ? `${data.machine.code} (${data.machine.name})` : 'No machine'}${data.line ? ` · ${data.line.name}` : ''}`}
            />

            <div className="grid gap-4 p-4 md:grid-cols-2">
              <div>
                {data.media.length > 0 ? (
                  <div className="space-y-3">
                    {data.media.map((item) => (
                      <figure key={item.id}>
                        <img
                          src={`/api/media/${item.mediaId}`}
                          alt={item.caption ?? `${humanise(item.role)} photo`}
                          className="w-full rounded-lg border border-steel-200"
                          loading="lazy"
                        />
                        <figcaption className="mt-1 text-xs font-semibold text-steel-600">
                          {humanise(item.role)}
                          {item.caption ? ` — ${item.caption}` : ''}
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                ) : (
                  <div className="grid h-full min-h-40 place-items-center rounded-lg border-2 border-dashed border-steel-300 p-6 text-center">
                    <div>
                      <BookOpen aria-hidden className="mx-auto size-8 text-steel-300" />
                      <p className="mt-2 text-sm font-semibold text-steel-600">No photos yet</p>
                      <p className="mt-1 text-xs text-steel-500">
                        A photo of the actual machine part works best. See the description for
                        the shots this lesson needs.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-xs font-extrabold tracking-widest text-steel-500 uppercase">
                  Key points
                </h3>
                <ol className="mt-2 space-y-2">
                  {(version.keyPoints ?? []).map((point, index) => (
                    <li key={index} className="flex gap-2 text-sm text-steel-800">
                      <span className="grid size-5 shrink-0 place-items-center rounded-full bg-steel-800 text-[11px] font-bold text-white">
                        {index + 1}
                      </span>
                      <span className="font-medium">{point}</span>
                    </li>
                  ))}
                </ol>
                {(version.keyPoints ?? []).length === 0 ? (
                  <p className="mt-2 text-sm text-steel-500">No key points recorded yet.</p>
                ) : null}
              </div>
            </div>

            {version.description ? (
              <div className="border-t border-steel-200 px-4 py-3">
                <p className="text-sm whitespace-pre-line text-steel-700">{version.description}</p>
              </div>
            ) : null}

            {version.safetyInfo || version.qualityInfo ? (
              <div className="space-y-2 border-t border-steel-200 p-4">
                {version.safetyInfo ? <SafetyWarning>{version.safetyInfo}</SafetyWarning> : null}
                {version.qualityInfo ? <QualityWarning>{version.qualityInfo}</QualityWarning> : null}
              </div>
            ) : null}

            <div className="grid gap-3 border-t border-steel-200 px-4 py-3 text-sm sm:grid-cols-2">
              <p>
                <span className="text-steel-500">Prepared by: </span>
                <span className="font-semibold text-steel-900">
                  {version.preparedByName ?? '—'}
                </span>
              </p>
              <p>
                <span className="text-steel-500">Approved by: </span>
                <span className="font-semibold text-steel-900">
                  {version.approvedByName ?? 'Not approved yet'}
                </span>
              </p>
              {version.postedLocation ? (
                <p className="sm:col-span-2">
                  <span className="text-steel-500">Posted at: </span>
                  <span className="font-semibold text-steel-900">{version.postedLocation}</span>
                </p>
              ) : null}
            </div>
          </Card>

          {/* Operator training action */}
          {canCompleteOwn && opl.status === 'PUBLISHED' ? (
            <Card className="p-4">
              {completed ? (
                <Callout tone="ok" title="Training recorded">
                  Your AM coach will confirm your competency on the line.
                </Callout>
              ) : (
                <>
                  <h3 className="text-sm font-bold text-steel-900">
                    Have you read and understood this lesson?
                  </h3>
                  <p className="mt-1 text-sm text-steel-500">
                    Recording it here keeps your training record up to date. Your coach confirms
                    competency separately, on the machine.
                  </p>
                  <Button
                    className="mt-3"
                    size="lg"
                    icon={ClipboardCheck}
                    loading={completeTraining.isPending}
                    onClick={() => completeTraining.mutate()}
                  >
                    Yes — I have read and understood it
                  </Button>
                </>
              )}
            </Card>
          ) : null}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Training" />
            <dl className="px-4 py-2">
              <DetailRow label="Assigned">
                {Object.values(data.trainingSummary).reduce((a, b) => a + b, 0)}
              </DetailRow>
              <DetailRow label="Competent">{data.trainingSummary.COMPETENT ?? 0}</DetailRow>
              <DetailRow label="Completed">
                {data.trainingSummary.TRAINING_COMPLETED ?? 0}
              </DetailRow>
              <DetailRow label="Viewed only">{data.trainingSummary.VIEWED ?? 0}</DetailRow>
              <DetailRow label="Needs retraining">
                {data.trainingSummary.NEEDS_RETRAINING ?? 0}
              </DetailRow>
            </dl>
          </Card>

          {data.linkedItems.length > 0 ? (
            <Card>
              <CardHeader
                title="Linked checklist tasks"
                subtitle="Operators see this lesson while doing these tasks"
              />
              <ul className="px-4 py-2">
                {data.linkedItems.map((item) => (
                  <li key={item.itemId} className="border-b border-steel-100 py-2 text-sm last:border-0">
                    <span className="font-semibold text-steel-500">#{item.seq}</span> {item.task}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          {data.linkedMachines.length > 0 ? (
            <Card>
              <CardHeader title="Applies to" />
              <ul className="px-4 py-2">
                {data.linkedMachines.map((machine) => (
                  <li key={machine.id} className="border-b border-steel-100 py-2 text-sm last:border-0">
                    <Link to={`/machines/${machine.id}`} className="font-semibold hover:underline">
                      {machine.code}
                    </Link>{' '}
                    <span className="text-steel-500">{machine.name}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <Card>
            <CardHeader title="Revisions" subtitle="Approved revisions are never overwritten" />
            <ul className="px-4 py-2">
              {data.revisions.map((revision) => (
                <li key={revision.id} className="border-b border-steel-100 py-2 last:border-0">
                  <Link
                    to={`/opl/${id}?versionId=${revision.id}`}
                    className="flex items-center justify-between gap-2 text-sm hover:underline"
                  >
                    <span className="font-semibold text-steel-900">
                      Revision {revision.revision}
                      {revision.isCurrent ? ' (current)' : ''}
                    </span>
                    <StatusPill
                      tone={oplStatusTone(revision.status)}
                      label={humanise(revision.status)}
                    />
                  </Link>
                  {revision.changeNote ? (
                    <p className="mt-0.5 text-xs text-steel-500">{revision.changeNote}</p>
                  ) : null}
                  <p className="text-xs text-steel-400">{formatDate(revision.createdAt)}</p>
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <CardHeader title="Dates" />
            <dl className="px-4 py-2">
              <DetailRow label="Effective">{formatDate(version.effectiveDate)}</DetailRow>
              <DetailRow label="Approved">{formatDateTime(version.approvedAt)}</DetailRow>
              <DetailRow label="Published">{formatDateTime(version.publishedAt)}</DetailRow>
            </dl>
          </Card>
        </div>
      </div>

      {assignOpen ? (
        <AssignTrainingModal oplId={id} onClose={() => setAssignOpen(false)} />
      ) : null}
    </div>
  );
}

function AssignTrainingModal({ oplId, onClose }: { oplId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['users', 'directory', 'OPERATOR'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; fullName: string; shift: string | null }> }>(
        '/api/users/directory?role=OPERATOR',
      ),
  });

  const assign = useMutation({
    mutationFn: () =>
      api.post(`/api/training/opl/${oplId}/assign`, { userIds: [...selected] }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not assign training.'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Assign this lesson"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            loading={assign.isPending}
            disabled={selected.size === 0}
            onClick={() => assign.mutate()}
          >
            Assign to {selected.size} operator{selected.size === 1 ? '' : 's'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {error ? <Callout tone="alarm">{error}</Callout> : null}
        <p className="text-sm text-steel-500">
          They get a notification and the lesson appears in their training list.
        </p>
        <ul className="divide-y divide-steel-100 rounded-lg border border-steel-200">
          {(data?.data ?? []).map((person) => (
            <li key={person.id}>
              <label className="flex min-h-12 cursor-pointer items-center gap-3 px-3">
                <input
                  type="checkbox"
                  className="size-5"
                  checked={selected.has(person.id)}
                  onChange={(event) => {
                    const next = new Set(selected);
                    if (event.target.checked) next.add(person.id);
                    else next.delete(person.id);
                    setSelected(next);
                  }}
                />
                <span className="text-sm font-medium text-steel-900">
                  {person.fullName}
                  {person.shift ? (
                    <span className="ml-2 text-xs text-steel-500">Shift {person.shift}</span>
                  ) : null}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

export { Field, Input, Select };
